import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const QUICK_NAV = [
  { anchor: "#quickstart", key: "helpCenter.navQuickstart", icon: "ri-rocket-2-line" },
  { anchor: "#faq", key: "helpCenter.navFaq", icon: "ri-question-answer-line" },
  { anchor: "#guides", key: "helpCenter.navDocs", icon: "ri-book-2-line" },
  { anchor: "#contact", key: "helpCenter.navContact", icon: "ri-customer-service-2-line" },
];

// Hot keywords are i18n keys whose values double as the search term, so each
// language searches its own FAQ copy.
const HOT_TAGS = [
  "helpCenter.hotTag1",
  "helpCenter.hotTag2",
  "helpCenter.hotTag3",
  "helpCenter.hotTag4",
  "helpCenter.hotTag5",
];

export default function HelpHero() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [term, setTerm] = useState("");

  const go = (value: string) => {
    const keyword = value.trim();
    const target = keyword ? `/help?q=${encodeURIComponent(keyword)}#faq` : "/help#faq";
    navigate(target);
    window.setTimeout(() => {
      document.getElementById("faq")?.scrollIntoView({ behavior: "smooth" });
    }, 80);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    go(term);
  };

  return (
    <section className="border-b border-background-200 bg-background-100">
      <div className="mx-auto w-full max-w-5xl px-4 py-12 md:px-6 md:py-16">
        <nav className="flex items-center gap-1.5 text-xs text-foreground-500">
          <Link to="/" className="cursor-pointer transition-colors hover:text-foreground-950">
            {t("helpCenter.breadcrumbHome")}
          </Link>
          <span className="flex h-3 w-3 items-center justify-center">
            <i className="ri-arrow-right-s-line" />
          </span>
          <span className="text-foreground-800">{t("helpCenter.breadcrumbCurrent")}</span>
        </nav>

        <span className="mt-4 inline-flex items-center gap-2 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-700">
          <span className="flex h-3 w-3 items-center justify-center">
            <i className="ri-lifebuoy-line" />
          </span>
          {t("helpCenter.badge")}
        </span>

        <h1 className="mt-3 font-heading text-3xl font-semibold tracking-tight text-foreground-950 md:text-4xl">
          {t("helpCenter.title")}
        </h1>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
          {t("helpCenter.subtitle")}
        </p>

        <form onSubmit={handleSubmit} className="mt-6 flex w-full max-w-2xl flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <span className="absolute left-3.5 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
              <i className="ri-search-line" />
            </span>
            <input
              type="search"
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              placeholder={t("helpCenter.searchPlaceholder")}
              aria-label={t("helpCenter.heroSearchLabel")}
              className="w-full rounded-md border border-background-300 bg-background-50 py-3 pl-10 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
            />
          </div>
          <button
            type="submit"
            className="inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-search-2-line" />
            </span>
            {t("helpCenter.heroSearchButton")}
          </button>
        </form>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="flex items-center gap-1.5 text-xs text-foreground-500">
            <span className="flex h-3.5 w-3.5 items-center justify-center text-accent-600">
              <i className="ri-fire-line" />
            </span>
            {t("helpCenter.heroSearchHot")}
          </span>
          {HOT_TAGS.map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => go(t(key))}
              className="cursor-pointer whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-3 py-1 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-foreground-950"
            >
              {t(key)}
            </button>
          ))}
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          {QUICK_NAV.map((item) => (
            <a
              key={item.anchor}
              href={item.anchor}
              className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-foreground-950"
            >
              <span className="flex h-4 w-4 items-center justify-center text-primary-700">
                <i className={item.icon} />
              </span>
              {t(item.key)}
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}